import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import type { ConnectionProviderKey } from "@forgepoint/shared";

export interface App {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  providerId: ConnectionProviderKey;
  actionIds: string[];
  triggerIds: string[];
}

interface AppState {
  isLoadingGet: boolean;
  getError: string | null;
}

interface AgentWorkflowAppsStore {
  apps: Map<string, App>;
  triggerAppIds: string[];
  actionAppIds: string[];
  appState: AppState;

  fetchApps: (filters?: {
    hasActions?: boolean;
    hasTriggers?: boolean;
  }) => Promise<void>;
  fetchTriggerApps: () => Promise<void>;
  fetchActionApps: () => Promise<void>;
  getApps: () => Map<string, App>;
  getTriggerApps: () => App[];
  getActionApps: () => App[];
  getAppById: (appId: string) => App | undefined;
}

export const useAgentWorkflowAppsStore = create<AgentWorkflowAppsStore>(
  (set, get) => ({
    apps: new Map(),
    triggerAppIds: [],
    actionAppIds: [],
    appState: {
      isLoadingGet: false,
      getError: null,
    },

    fetchApps: async (filters) => {
      set({
        appState: { isLoadingGet: true, getError: null },
      });

      try {
        let url = "apps";
        if (filters) {
          const params = new URLSearchParams();
          if (filters.hasActions !== undefined) {
            params.append("hasActions", String(filters.hasActions));
          }
          if (filters.hasTriggers !== undefined) {
            params.append("hasTriggers", String(filters.hasTriggers));
          }
          if (params.toString()) {
            url += `?${params.toString()}`;
          }
        }

        const response = await fetchApi(url, "GET");

        if (response.ok && response.data) {
          const data = response.data as { apps: App[] };
          const appsMap = new Map<string, App>();
          data.apps.forEach((app) => {
            appsMap.set(app.id, app);
          });
          set({
            apps: appsMap,
            appState: { isLoadingGet: false, getError: null },
          });
        } else {
          set({
            appState: {
              isLoadingGet: false,
              getError: "Failed to fetch apps",
            },
          });
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to fetch apps";
        captureException(error as Error);
        set({
          appState: { isLoadingGet: false, getError: errorMessage },
        });
      }
    },

    fetchTriggerApps: async () => {
      const { fetchApps } = get();
      await fetchApps({ hasTriggers: true });

      // Update triggerAppIds with the fetched apps that have triggers
      const apps = get().apps;
      const triggerAppIds = Array.from(apps.values())
        .filter((app) => app.triggerIds.length > 0)
        .map((app) => app.id);

      set({ triggerAppIds });
    },

    fetchActionApps: async () => {
      const { fetchApps } = get();
      await fetchApps({ hasActions: true });

      // Update actionAppIds with the fetched apps that have actions
      const apps = get().apps;
      const actionAppIds = Array.from(apps.values())
        .filter((app) => app.actionIds.length > 0)
        .map((app) => app.id);

      set({ actionAppIds });
    },

    getApps: () => get().apps,

    getTriggerApps: () => {
      const { apps, triggerAppIds } = get();
      return triggerAppIds
        .map((id) => apps.get(id))
        .filter((app): app is App => app !== undefined);
    },

    getActionApps: () => {
      const { apps, actionAppIds } = get();
      return actionAppIds
        .map((id) => apps.get(id))
        .filter((app): app is App => app !== undefined);
    },

    getAppById: (appId: string) => get().apps.get(appId),
  }),
);
